import { createInstancedRenderer } from "./instancedRenderer";
import { paintRects } from "../canvas2d/paintRects";

export const RECT_WIDTH_SLOTS = 4;
export const RECT_PALETTE_SLOTS = 8;

// Instance: [x, y0, y1] in data units (bar index, price), [shape slot, colour slot]
const RECT_ATTRIBUTE_COMPONENTS = [3, 2];

// Positions are converted to device pixels by uX / uY (value * scale + offset)
// and snapped to whole pixels, so edges stay sharp. While the view follows
// time uSnapX is 0 and x stays sub-pixel, so all bars move evenly together
// instead of each hopping a pixel at its own moment. A shape slot gives the
// width in whole device pixels and the offset of the left edge from the
// bar x, so wicks and bodies share one centre and bar ticks sit beside
// the stem. A rectangle is at least uMinHeight device pixels tall, grown
// around its centre, so a bar tick or a doji sits on its price.
const RECT_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec3 aRect;
layout(location = 2) in vec2 aStyle;

uniform vec2 uViewport;
uniform vec2 uX;
uniform vec2 uY;
uniform float uMinHeight;
uniform float uSnapX;
uniform float uWidths[${RECT_WIDTH_SLOTS}];
uniform float uOffsets[${RECT_WIDTH_SLOTS}];
uniform vec4 uPalette[${RECT_PALETTE_SLOTS}];

out vec4 vColor;

void main() {
  int shape = int(aStyle.x);
  float width = uWidths[shape];
  float xDevice = aRect.x * uX.x + uX.y;
  float left = mix(xDevice, floor(xDevice), uSnapX) + uOffsets[shape];
  float yFirst = floor(aRect.y * uY.x + uY.y + 0.5);
  float ySecond = floor(aRect.z * uY.x + uY.y + 0.5);
  float height = max(abs(ySecond - yFirst), uMinHeight);
  float top = floor((yFirst + ySecond - height) * 0.5);
  float bottom = top + height;
  vec2 position = vec2(left + aCorner.x * width, mix(top, bottom, aCorner.y));
  vec2 clip = position / uViewport * 2.0 - 1.0;

  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vColor = uPalette[int(aStyle.y)];
}
`;

/**
 * Instanced rectangles: candle bodies and wicks, OHLC bars, position zones
 * and indicator histograms. Shapes and colours come from small uniform tables, so a zoom
 * or a colour change does not touch the instance buffer.
 */
export const createRectRenderer = () => {
  const renderer = createInstancedRenderer({
    vertexShader: RECT_VERTEX_SHADER,
    uniformNames: ["uViewport", "uX", "uY", "uMinHeight", "uSnapX", "uWidths", "uOffsets", "uPalette"],
    attributeComponents: RECT_ATTRIBUTE_COMPONENTS,
  });

  return {
    floatsPerInstance: renderer.floatsPerInstance,
    init: renderer.init,
    release: renderer.release,
    setInstances: renderer.setInstances,
    updateInstances: renderer.updateInstances,

    /**
     * @param {object} params
     * @param {number[]} params.viewport   - [width, height] of the canvas, device px
     * @param {number[]} params.xTransform - [scale, offset]: data x → device px
     * @param {number[]} params.yTransform - [scale, offset]: data y → device px
     * @param {{ widths: Float32Array, offsets: Float32Array, minHeight: number }} params.shapes -
     *   RECT_WIDTH_SLOTS widths and left-edge offsets, the minimal height; device px
     * @param {Float32Array} params.palette - RECT_PALETTE_SLOTS RGBA colours
     * @param {boolean} [params.isSnappedX=true] - false keeps x sub-pixel (while the view follows time)
     */
    draw: (params) => {
      const { viewport, xTransform, yTransform, shapes, palette, isSnappedX = true } = params;

      renderer.draw(
        (gl, uniforms) => {
          gl.uniform2fv(uniforms.uViewport, viewport);
          gl.uniform2fv(uniforms.uX, xTransform);
          gl.uniform2fv(uniforms.uY, yTransform);
          gl.uniform1f(uniforms.uMinHeight, shapes.minHeight);
          gl.uniform1f(uniforms.uSnapX, isSnappedX ? 1 : 0);
          gl.uniform1fv(uniforms.uWidths, shapes.widths);
          gl.uniform1fv(uniforms.uOffsets, shapes.offsets);
          gl.uniform4fv(uniforms.uPalette, palette);
        },
        (context, instances) => paintRects(context, instances, renderer.floatsPerInstance, params),
      );
    },
  };
};
